import { FC, useState } from "react";
import { Controller, useFieldArray } from "react-hook-form";
import { GroupBase, OptionsOrGroups } from "react-select";
import ReactInputMask from "react-input-mask";
import {format, parse} from "date-fns";
import DatePicker from "react-datepicker";

import HubInputField from "../../inputType";
import AddCasePhoneNumber from "../addCasePhoneNumber";

import { AddCasePatientInformaitonProps } from "./types";
import { PATIENT_GENDER, PATIENT_TIME_TO_CONTACT, PATIENT_TITLE } from "src/constants/cases";
import { PatientPhoneNumber } from "src/pages/addCase/types";



const AddCasePatientInformation: FC<AddCasePatientInformaitonProps> = ({ control, watch }): JSX.Element => {
  const [openDatePicker, changeDatePickerVisibility] = useState<boolean>(false)

  const { append: appendPhoneNumber, remove: removePhoneNumber } = 
    useFieldArray({ control, name: "patient_attributes.phone_numbers" });
  const phoneNumberListing = watch("patient_attributes.phone_numbers") || [];

  const addNewPhoneNumber = (): void => {
    const newPhoneNumber = {
      id: null,
      phone_type: "cell",
      is_ok_to_leave_message: false,
      is_preferred: phoneNumberListing.length === 0,
      number: "",
    } as PatientPhoneNumber;;
    appendPhoneNumber(newPhoneNumber);
  };

  return (
    <div className="card mb-3">
      <div className="card-header bg-success-subtle">
        <h4 className="text-black m-0">Patient Information</h4>
      </div>
      <div className="card-body">
        <Controller
          name="patient_attributes.title"
          control={control}
          defaultValue=""
          render={({ field, fieldState: { error } }) => (
            <HubInputField
              inputId="patient_attributes.title"
              inputLabel="Title"
              isRequired={false}
              isRowBased={true}
              field={field}
              error={error}
              isDropdown={true}
              isSearchable={false}
              isTypeAhead
              dropdownData={PATIENT_TITLE as unknown as OptionsOrGroups<string, GroupBase<string>>}
            />
          )}
        />

        <div className="row">
          <label className="form-label col-lg-2 col-md-4 col-12">Patient Name *</label>
          <div className="col-lg-10 col-md-8 col-12">
            <div className="row">
              <div className="col-lg-4 col-md-4 col-12">
                <Controller
                  name="patient_attributes.firstname"
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <HubInputField
                      inputId="patient_attributes.firstname"
                      isRowBased={true}
                      isRequired={false}
                      field={field}
                      inputPlaceholder="First Name *"
                      error={error}
                    />
                  )}
                />
              </div>
              <div className="col-lg-4 col-md-4 col-12">
                <Controller
                  name="patient_attributes.middlename"
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <HubInputField
                      isRowBased={true}
                      isRequired={false}
                      field={field}
                      inputId="patient_attributes.middlename"
                      inputPlaceholder="Middle Name"
                      error={error}
                    />
                  )}
                />
              </div>
              <div className="col-lg-4 col-md-4 col-12">
                <Controller
                  name="patient_attributes.lastname"
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <HubInputField
                      isRowBased={true}
                      isRequired={false}
                      field={field}
                      inputId="patient_attributes.lastname"
                      inputPlaceholder="Last Name *"
                      error={error}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <label className="form-label col-lg-2 col-md-4 col-12">Gender *</label>
          <div className="col-lg-2 col-md-8 col-12" onFocus={() => changeDatePickerVisibility(false)}>
            <Controller
              name="patient_attributes.gender"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <HubInputField
                  inputId="patient_attributes.gender"
                  isRequired={true}
                  isRowBased={true}
                  field={field}
                  error={error}
                  isDropdown={true}
                  isSearchable={false}
                  isTypeAhead
                  dropdownData={PATIENT_GENDER as unknown as OptionsOrGroups<string, GroupBase<string>>}
                />
              )}
            />
          </div>
          <div className="col-lg-4 col-md-12 col-12">
            <Controller
              name="patient_attributes.dob"
              control={control}
              defaultValue=""
              render={({field, fieldState: {error}}) => (
                  <div className="row align-items-center mb-3">
                      <label htmlFor="patient_attributes.dob" className="col-12 col-md-4 form-label text-end mb-0">
                          DoB *
                      </label>
                      <div className="col-12 col-md-8 position-relative">
                          <DatePicker
                              selected={
                                  field.value
                                      ? parse(field.value, "yyyy-MM-dd", new Date()) // Parse default yyyy-MM-dd to a Date
                                      : null
                              }
                              onChange={(date) => {
                                  field.onChange(date ? format(date, "yyyy-MM-dd") : ""); // Convert back to yyyy-MM-dd for consistency
                              }}
                              open={openDatePicker}
                              onClickOutside={() =>
                                  changeDatePickerVisibility(false)
                              }
                              onFocus={() => changeDatePickerVisibility(true)}
                              onBlur={() => changeDatePickerVisibility(false)}
                              onKeyDown={(e) => {
                                if (e.key === "Tab" || e.key === "Escape") {
                                  changeDatePickerVisibility(false);
                                }
                              }}
                              dateFormat="MM/dd/yyyy" // User-facing format
                              className={`form-control ${error?.message ? "is-invalid" : ""}`}
                              placeholderText="mm/dd/yyyy"
                              showMonthDropdown
                              showYearDropdown
                              customInput={
                                  <ReactInputMask mask="99/99/9999">
                                      {(inputProps: {}) => <input {...inputProps} />}
                                  </ReactInputMask>
                              }
                              maxDate={new Date()} // Restrict future dates
                              wrapperClassName="w-100"
                          />
                          <i
                              className="bi bi-calendar position-absolute"
                              style={{
                                  right: error?.message ? "50px" : "25px", // Adjust position based on error
                                  top: "18px",
                                  transform: "translateY(-50%)",
                                  pointerEvents: "none",
                              }}
                          ></i>
                          {error && error.message ? (
                              <div
                                  dangerouslySetInnerHTML={{__html: error.message}}
                                  className="text-danger text-small mt-2"
                              ></div>
                          ) : null}
                      </div>

                  </div>
              )}
            />
          </div>
          <div className="col-lg-4 col-md-12 col-12" onFocus={() => changeDatePickerVisibility(false)}>
            <Controller
                name="patient_attributes.ssn"
                control={control}
                defaultValue=""
                render={({field, fieldState: {error}}) => (
                    <div className="row mb-3">
                      <label htmlFor="patient_attributes.ssn" className="form-label col-lg-4 col-md-4 col-12">
                        SSN
                      </label>
                      <div className="col-lg-8 col-md-8 col-12">
                        <ReactInputMask
                            {...field}
                            mask="999-99-9999"
                            onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                        >
                          {(inputProps: {}) => <input type="text" id="patient_attributes.ssn"
                                                      className="form-control"
                                                      placeholder="123-45-6789" {...inputProps} />}
                        </ReactInputMask>

                        {error && error.message ? (
                            <div dangerouslySetInnerHTML={{__html: error.message}}
                                 className="text-danger text-small mt-2"></div>
                        ) : null}
                      </div>
                    </div>
                )}
            />
          </div>
        </div>

       <div onFocus={() => changeDatePickerVisibility(false)}> 
        <Controller
          name="patient_attributes.best_time_to_contact"
          control={control}
          defaultValue=""
          render={({ field, fieldState: { error } }) => (
            <HubInputField
              inputId="patient_attributes.best_time_to_contact"
              inputLabel="Best Time to Contact"
              isRequired={false}
              isRowBased={true}
              field={field}
              error={error}
              isTypeAhead
              isDropdown={true}
              isSearchable={false}
              dropdownData={PATIENT_TIME_TO_CONTACT as unknown as OptionsOrGroups<string, GroupBase<string>>}
            />
          )}
        /></div>

        {phoneNumberListing.map((_d, index) => (
          <AddCasePhoneNumber
            key={index}
            control={control}
            phoneNumberIndex={index}
            removePhoneNumber={(index) => removePhoneNumber(index)}
          />
        ))}

        <div className="text-end mt-lg-2 mt-md-2 mt-0">
          <button type="button" className="btn btn-success" onClick={addNewPhoneNumber}>
            <i className="fas fa-plus"></i> Add Phone Number
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCasePatientInformation;
